import React, { useEffect } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import { useCart } from "../../components/CartContext";

import { fetchPriceSummary } from "../../actions/priceActions";

import "./css/CheckoutScreen.css";


function CheckoutScreen() {

  const navigate = useNavigate();

  const dispatch = useDispatch();


  const {
    restaurantId,
    tableId,
  } = useParams();


  const {
    cart,
    cartCount,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
  } = useCart();


  // =========================================================
  // PRICE SUMMARY FROM BACKEND
  // =========================================================

  const {
    loading: priceLoading,
    summary: priceSummary,
    error: priceError,
  } = useSelector(
    (state) => state.price
  );


  // =========================================================
  // GET PRICE SUMMARY
  // =========================================================

  useEffect(() => {

    if (cart.length === 0) {
      return;
    }


    const items = cart.map((item) => ({

      menu_item_id:
        item.id,

      quantity:
        item.quantity,

    }));


    dispatch(
      fetchPriceSummary(
        restaurantId,
        items
      )
    );

  }, [
    dispatch,
    restaurantId,
    cart,
  ]);


  // =========================================================
  // PROCEED TO ORDER
  // =========================================================

  const handleProceedToOrder = () => {

    navigate(
      `/${restaurantId}/table/${tableId}/order`
    );

  };


  // =========================================================
  // EMPTY CART
  // =========================================================

  if (cart.length === 0) {

    return (

      <div className="checkout-page">

        <div className="checkout-container">


          {/* HEADER */}

          <div className="checkout-header">

            <button
              className="checkout-back-button"
              onClick={() =>
                navigate(
                  `/${restaurantId}/table/${tableId}`
                )
              }
            >
              ← Back to Menu
            </button>


            <div className="checkout-title">

              <h1>
                Checkout
              </h1>

              <p>
                Review your order before continuing
              </p>

            </div>

          </div>


          {/* EMPTY CART */}

          <div className="checkout-card">

            <div className="checkout-empty">

              <div className="checkout-empty-icon">
                🛒
              </div>


              <h3>
                Your cart is empty
              </h3>


              <p>
                Please add some items from the menu.
              </p>


              <button
                onClick={() =>
                  navigate(
                    `/${restaurantId}/table/${tableId}`
                  )
                }
              >
                Browse Menu
              </button>

            </div>

          </div>

        </div>

      </div>

    );

  }


  // =========================================================
  // CHECKOUT
  // =========================================================

  return (

    <div className="checkout-page">

      <div className="checkout-container">


        {/* =================================================
            HEADER
        ================================================= */}

        <div className="checkout-header">

          <button
            className="checkout-back-button"
            onClick={() =>
              navigate(
                `/${restaurantId}/table/${tableId}`
              )
            }
          >
            ← Back to Menu
          </button>


          <div className="checkout-title">

            <h1>
              Checkout
            </h1>


            <p>
              Review your order before continuing
            </p>

          </div>

        </div>


        {/* =================================================
            ORDER CARD
        ================================================= */}

        <div className="checkout-card">


          <div className="checkout-card-header">

            <h2>
              Your Order
            </h2>


            <span>

              {cartCount}{" "}

              {cartCount === 1
                ? "item"
                : "items"}

            </span>

          </div>


          {/* =================================================
              ORDER ITEMS
          ================================================= */}

          <div className="checkout-items">

            {cart.map((item) => {

              const itemTotal =
                Number(item.price) *
                item.quantity;


              return (

                <div
                  className="checkout-item"
                  key={item.id}
                >


                  {/* IMAGE */}

                  <img
                    src={item.image}
                    alt={item.name}
                    className="checkout-item-image"
                  />


                  {/* DETAILS */}

                  <div className="checkout-item-details">

                    <h3>
                      {item.name}
                    </h3>


                    {item.description && (

                      <p>
                        {item.description}
                      </p>

                    )}


                    <span className="checkout-item-price">

                      ₹

                      {Number(
                        item.price
                      ).toFixed(2)}

                    </span>

                  </div>


                  {/* ACTIONS */}

                  <div className="checkout-item-actions">


                    <strong className="checkout-item-total">

                      ₹

                      {itemTotal.toFixed(2)}

                    </strong>


                    <div className="checkout-quantity">

                      <button
                        type="button"
                        onClick={() =>
                          decreaseQuantity(
                            item.id
                          )
                        }
                      >
                        −
                      </button>


                      <span>
                        {item.quantity}
                      </span>


                      <button
                        type="button"
                        onClick={() =>
                          increaseQuantity(
                            item.id
                          )
                        }
                      >
                        +
                      </button>

                    </div>


                    <button
                      type="button"
                      className="checkout-remove"
                      onClick={() =>
                        removeFromCart(
                          item.id
                        )
                      }
                    >
                      Remove
                    </button>

                  </div>

                </div>

              );

            })}

          </div>

        </div>


        {/* =================================================
            SUMMARY
        ================================================= */}

        <div className="checkout-summary">


          {/* ITEMS */}

          <div className="summary-row">

            <span>
              Items
            </span>


            <span>
              {cartCount}
            </span>

          </div>


          {/* SUBTOTAL */}

          <div className="summary-row">

            <span>
              Subtotal
            </span>


            <span>

              {priceLoading

                ? "Calculating..."

                : `₹${Number(
                    priceSummary?.subtotal || 0
                  ).toFixed(2)}`}

            </span>

          </div>


          {/* TAX */}

          {priceSummary && (

            <div className="summary-row">

              <span>
                Tax
              </span>


              <span>

                ₹

                {Number(
                  priceSummary.tax || 0
                ).toFixed(2)}

              </span>

            </div>

          )}


          {/* SERVICE CHARGE */}

          {priceSummary && (

            <div className="summary-row">

              <span>
                Service Charge
              </span>


              <span>

                ₹

                {Number(
                  priceSummary.service_charge || 0
                ).toFixed(2)}

              </span>

            </div>

          )}


          {/* DISCOUNT */}

          {priceSummary &&
            Number(
              priceSummary.discount || 0
            ) > 0 && (

              <div className="summary-row">

                <span>
                  Discount
                </span>


                <span>

                  -₹

                  {Number(
                    priceSummary.discount
                  ).toFixed(2)}

                </span>

              </div>

            )}


          <div className="summary-divider" />


          {/* TOTAL */}

          <div className="summary-total">

            <span>
              Total Amount
            </span>


            <strong>

              {priceLoading

                ? "Calculating..."

                : `₹${Number(
                    priceSummary?.total || 0
                  ).toFixed(2)}`}

            </strong>

          </div>


          {/* ERROR */}

          {priceError && (

            <p>
              {priceError}
            </p>

          )}


          {/* PROCEED */}

          <button
            type="button"
            className="proceed-order-button"
            onClick={handleProceedToOrder}
            disabled={
              priceLoading ||
              !priceSummary
            }
          >

            Proceed to Order Creation

            <span>
              →
            </span>

          </button>


        </div>


      </div>

    </div>

  );

}


export default CheckoutScreen;